const DEFAULT_GRID_SIZE = 10
const DEFAULT_FIELD_WIDTH = 180
const DEFAULT_FIELD_HEIGHT = 64
const MIN_FIELD_WIDTH = 120
const MIN_FIELD_HEIGHT = 52

export const CANVAS_DEFAULTS = {
  gridSize: DEFAULT_GRID_SIZE,
  defaultFieldWidth: DEFAULT_FIELD_WIDTH,
  defaultFieldHeight: DEFAULT_FIELD_HEIGHT,
  minFieldWidth: MIN_FIELD_WIDTH,
  minFieldHeight: MIN_FIELD_HEIGHT,
}

export const toNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const snapValue = (value, gridSize = DEFAULT_GRID_SIZE, enabled = false) => {
  const safeValue = toNumber(value, 0)
  if (!enabled) return Math.round(safeValue)

  const safeGrid = Math.max(1, toNumber(gridSize, DEFAULT_GRID_SIZE))
  return Math.round(safeValue / safeGrid) * safeGrid
}

export const normalizeFieldGeometry = (field, options = {}) => {
  const {
    defaultWidth = DEFAULT_FIELD_WIDTH,
    defaultHeight = DEFAULT_FIELD_HEIGHT,
    minWidth = MIN_FIELD_WIDTH,
    minHeight = MIN_FIELD_HEIGHT,
  } = options

  return {
    x: toNumber(field?.x, 0),
    y: toNumber(field?.y, 0),
    width: Math.max(minWidth, toNumber(field?.width, defaultWidth)),
    height: Math.max(minHeight, toNumber(field?.height, defaultHeight)),
  }
}

export const clampFieldRect = (
  field,
  updates,
  canvasRect,
  options = {}
) => {
  const {
    minWidth = MIN_FIELD_WIDTH,
    minHeight = MIN_FIELD_HEIGHT,
    gridSize = DEFAULT_GRID_SIZE,
    snapToGrid = false,
    defaultWidth = DEFAULT_FIELD_WIDTH,
    defaultHeight = DEFAULT_FIELD_HEIGHT,
  } = options

  const source = normalizeFieldGeometry(field, {
    defaultWidth,
    defaultHeight,
    minWidth,
    minHeight,
  })

  const nextWidth = Math.max(
    minWidth,
    toNumber(updates?.width, source.width)
  )
  const nextHeight = Math.max(
    minHeight,
    toNumber(updates?.height, source.height)
  )

  const canvasWidth = Math.max(0, toNumber(canvasRect?.width, 0))
  const canvasHeight = Math.max(0, toNumber(canvasRect?.height, 0))

  const maxX = Math.max(0, canvasWidth - nextWidth)
  const maxY = Math.max(0, canvasHeight - nextHeight)

  const nextX = Math.max(
    0,
    Math.min(toNumber(updates?.x, source.x), maxX)
  )
  const nextY = Math.max(
    0,
    Math.min(toNumber(updates?.y, source.y), maxY)
  )

  const width = snapValue(nextWidth, gridSize, snapToGrid)
  const height = snapValue(nextHeight, gridSize, snapToGrid)
  const maxSnappedX = Math.max(0, canvasWidth - width)
  const maxSnappedY = Math.max(0, canvasHeight - height)

  return {
    x: Math.max(0, Math.min(snapValue(nextX, gridSize, snapToGrid), maxSnappedX)),
    y: Math.max(0, Math.min(snapValue(nextY, gridSize, snapToGrid), maxSnappedY)),
    width,
    height,
  }
}

export const normalizeName = (raw) =>
  String(raw || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_|_$/g, '')
